/* Keep the full cart on the identified Klaviyo profile; native Shopify events remain the flow trigger. */
(function () {
  'use strict';
  function recoveryItems(items) {
    return items.map(item => {
      const line = {id:Number(item.variant_id || item.id),quantity:item.quantity};
      const properties = Object.fromEntries(Object.entries(item.properties || {}).filter(([key, value]) => !['__proto__','constructor','prototype'].includes(key) && typeof value === 'string'));
      if (Object.keys(properties).length) line.properties = properties;
      const plan = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan;
      if (plan && /^\d+$/.test(String(plan))) line.selling_plan = Number(plan);
      return line;
    });
  }
  function discountCodes(cart) {
    return [...new Set([...(cart.discount_codes || []).filter(d => d.applicable !== false).map(d => d.code), ...(cart.cart_level_discount_applications || []).filter(d => d.type === 'discount_code').map(d => d.title)].filter(code => typeof code === 'string' && code.length <= 255).map(code => code.toUpperCase()))];
  }
  function packRecovery(data) {
    const bytes = new TextEncoder().encode(JSON.stringify(data));
    return btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join('')).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
  }
  function unpackRecovery(value) {
    if (!/^[A-Za-z0-9_-]{1,30000}$/.test(value || '')) throw new Error('invalid-recovery');
    const raw = atob(value.replace(/-/g,'+').replace(/_/g,'/'));
    const data = JSON.parse(new TextDecoder('utf-8',{fatal:true}).decode(Uint8Array.from(raw, c => c.charCodeAt(0))));
    if (data.v !== 1 || !Array.isArray(data.items) || !data.items.length || data.items.length > 100) throw new Error('invalid-items');
    for (const item of data.items) {
      if (!Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 9999) throw new Error('invalid-line');
      if (item.properties && (typeof item.properties !== 'object' || Array.isArray(item.properties) || Object.entries(item.properties).some(([key,value]) => ['__proto__','constructor','prototype'].includes(key) || key.length > 255 || typeof value !== 'string' || value.length > 4096))) throw new Error('invalid-properties');
      if (item.selling_plan && (!Number.isSafeInteger(item.selling_plan) || item.selling_plan <= 0)) throw new Error('invalid-plan');
    }
    return {v:1,items:recoveryItems(data.items),codes:Array.isArray(data.codes) ? data.codes.filter(c => typeof c === 'string' && c.length <= 255).slice(0,5) : []};
  }
  async function restoreCart(request, data) {
    const previous = await request('cart.js');
    const codes = [...new Set([...data.codes,...discountCodes(previous)].map(code => code.toUpperCase()))].slice(0,5);
    try {
      await request('cart/clear.js',{});
      await request('cart/add.js',{items:data.items});
      if (codes.length) await request('cart/update.js',{discount:codes.join(',')});
      return await request('cart.js');
    } catch (error) {
      // Keep the visitor's previous basket if a variant is no longer available.
      await request('cart/clear.js',{});
      if (previous.items?.length) await request('cart/add.js',{items:recoveryItems(previous.items)});
      const oldCodes = discountCodes(previous);
      if (oldCodes.length) await request('cart/update.js',{discount:oldCodes.join(',')});
      throw error;
    }
  }
  function cartProperties(cart) {
    const items = (cart.items || []).filter(item => /^\d+$/.test(String(item.variant_id || item.id)) && Number.isInteger(item.quantity) && item.quantity > 0);
    const quantities = new Map();
    for (const item of items) {
      const id = String(item.variant_id || item.id);
      quantities.set(id, (quantities.get(id) || 0) + item.quantity);
    }
    const primary = items.reduce((best, item) => !best || Number(item.final_price ?? item.price ?? 0) > Number(best.final_price ?? best.price ?? 0) ? item : best, null);
    // A Shopify permalink drops per-line properties and can turn bundle gifts into paid items.
    const path = quantities.size ? '/cart?atelier_restore=' + packRecovery({v:1,items:recoveryItems(items),codes:discountCodes(cart)}) : '/cart';
    const image = primary && (primary.featured_image?.url || primary.image) || '';
    return {
      AtelierCartReady: !!primary,
      AtelierCartEmpty: !primary,
      AtelierCartRecoveryPath: path,
      AtelierCartRecoveryEncoded: encodeURIComponent(path),
      AtelierCartPrimaryProduct: primary ? {
        'Product Name': primary.product_title || primary.title || '',
        'Variant Name': primary.variant_title === 'Default Title' ? '' : primary.variant_title || '',
        ImageURL: image.startsWith('//') ? 'https:' + image : image,
        Price: Number(primary.final_price ?? primary.price ?? 0) / 100,
        Quantity: quantities.get(String(primary.variant_id || primary.id)),
        VariantID: String(primary.variant_id || primary.id),
        '$currency': cart.currency || 'EUR'
      } : {},
      AtelierCartItemCount: items.reduce((sum, item) => sum + item.quantity, 0)
    };
  }
  if (typeof module === 'object' && module.exports) { module.exports = {cartProperties,packRecovery,unpackRecovery,restoreCart}; return; }
  if (window.__atelierCartProfileInstalled) return;
  window.__atelierCartProfileInstalled = true;
  const root = document.documentElement;
  const status = value => { root.dataset.atelierCartSync = value; };
  let pending = true, busy = false, restoring = false, lastPayload = '', wasIdentified = false, timer;
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + path, {method:body ? 'POST' : 'GET',credentials:'same-origin',headers:{Accept:'application/json',...(body ? {'Content-Type':'application/json'} : {})},cache:'no-store',signal:controller.signal,...(body ? {body:JSON.stringify(body)} : {})});
      if (!response.ok) throw new Error('cart-unavailable');
      return await response.json();
    } finally {clearTimeout(timeout);}
  }
  async function resumeBasket() {
    const url = new URL(location.href), encoded = url.searchParams.get('atelier_restore');
    if (!encoded) return;
    restoring = true;
    const notice = document.createElement('div');
    notice.setAttribute('role','status');
    notice.style.cssText = 'position:fixed;inset:0;z-index:2147483646;background:#faf7f0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px;text-align:center;color:#4b3023;font:20px Georgia,serif;gap:24px';
    notice.textContent = 'Nous préparons votre panier…';
    document.body.appendChild(notice);
    try {
      const data = unpackRecovery(encoded);
      const coupon = url.searchParams.get('atelier_discount');
      if (coupon && /^[A-Za-z0-9_-]{1,255}$/.test(coupon)) data.codes = [...new Set([coupon.toUpperCase(),...data.codes])].slice(0,5);
      await restoreCart(request,data);
      url.searchParams.delete('atelier_restore');
      url.searchParams.delete('atelier_discount');
      url.searchParams.set('atelier_restored','1');
      location.replace(url.pathname + url.search + url.hash);
    } catch (_) {
      status('recovery-error');
      notice.textContent = 'Votre panier n’a pas pu être rechargé. Vous pouvez réessayer ou retrouver votre panier actuel.';
      const retry = document.createElement('button');
      retry.textContent = 'Réessayer';retry.onclick = () => location.reload();
      const back = document.createElement('a');
      back.textContent = 'Voir mon panier';back.href = (window.Shopify?.routes?.root || '/') + 'cart';
      notice.append(retry,back);
    }
  }
  let privacyJob;
  function privacy() {
    if (window.Shopify?.customerPrivacy) return Promise.resolve(window.Shopify.customerPrivacy);
    if (!privacyJob && window.Shopify?.loadFeatures) privacyJob = new Promise(resolve => {
      const timeout = setTimeout(() => { privacyJob = null; resolve(null); }, 4000);
      window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
        clearTimeout(timeout); const api = error ? null : window.Shopify.customerPrivacy;
        if (!api) privacyJob = null; resolve(api);
      });
    });
    return privacyJob || Promise.resolve(null);
  }
  function bounded(promise) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('timeout')), 6000);
      Promise.resolve(promise).then(value => {clearTimeout(timeout);resolve(value);}, error => {clearTimeout(timeout);reject(error);});
    });
  }
  async function sync() {
    if (busy || restoring || document.hidden) return;
    busy = true;
    try {
      const consent = await privacy();
      if (!consent || !consent.analyticsProcessingAllowed() || !consent.marketingAllowed()) { status('consent-required'); return; }
      if (typeof window.klaviyo?.isIdentified !== 'function') {status('sdk-pending');return;}
      const identified = await bounded(window.klaviyo.isIdentified());
      if (!identified) {wasIdentified = false;status('identity-required');return;}
      if (!wasIdentified) {pending = true;lastPayload = '';}
      wasIdentified = true;
      if (!pending) return;
      pending = false;
      const cart = await request('cart.js');
      const props = cartProperties(cart), payload = JSON.stringify(props);
      if (payload !== lastPayload) {
        // The SDK reuses the identified visitor. No email is read or stored by this module.
        await bounded(window.klaviyo.identify({...props, AtelierCartUpdatedAt:new Date().toISOString()}));
        lastPayload = payload;
      }
      status(props.AtelierCartReady ? 'synced' : 'empty');
    } catch (_) {pending = true;status('retry-pending');}
    finally {busy = false;}
  }
  function schedule() {pending = true;clearTimeout(timer);timer = setTimeout(sync, 350);}
  ['cart:change','cart:refresh','atelier:klaviyo-identified','visitorConsentCollected'].forEach(name => document.addEventListener(name, schedule));
  window.addEventListener('klaviyoForms', event => {if (event.detail?.type === 'submit') schedule();});
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => {if (!document.hidden) schedule();});
  // Observe completed cart mutations from app drawers without patching fetch or XMLHttpRequest.
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(list => {
        if (list.getEntries().some(entry => {try {const url = new URL(entry.name);return url.origin === location.origin && /\/cart\/(?:add|change|update|clear)(?:\.js)?$/.test(url.pathname);} catch (_) {return false;}})) schedule();
      }).observe({type:'resource',buffered:false});
    } catch (_) {}
  }
  // Also pick up identification by embedded forms, Shopify login and tracked email links.
  setInterval(sync, 5000);
  resumeBasket();
  schedule();
})();
